/* Extern */
import React, { Component } from 'react';
import {
  Icon,
  Image,
  Dropdown
} from 'semantic-ui-react';
import Link from 'next/link';
import { withRouter } from 'next/router';
import { inject, observer } from 'mobx-react';
import { Avatar } from '@classapp-tech/edna';

/* Intern */
import { asset } from '../lib/Util';
import { getNodeEnv } from '../lib/env';
import Config from '../lib/Config';
import Loading from './Loading';

/* Models */
import User from '../models/User';

/**
 * @param {string} name
 * @param {JSX.Element} logo
 */
const trigger = (name, logo) => (
  <div className="side-menu-dropdown-trigger" style={{ display: 'flex', alignItems: 'center' }}>
    {logo}
  </div>
);

/**
 * @typedef {{
 *  id: number,
 *  key: number,
 *  value: string,
 *  text: string,
 *  image: JSX.Element,
 *  organization: import('../pages/_app').OrganizationProps,
 * }} OrganizationSideMenuItem
 */

/**
 * @typedef {{
 *  name: string,
 *  routes: string[],
 *  hover: string,
 *  key: string | number,
 *  icon: string,
 *  image?: string,
 * }} SideMenuItem
 */

/**
 * @typedef {{
 *  store?: import('../lib/store').Store,
 *  router: import('next/router').Router,
 * }} Props
 * @extends {Component<Props>}
 */
class SideMenu extends Component {
  /**
   * @param {Props} props
   */
  constructor(props) {
    super(props);
    this.state = {
      currentHover: null,
      loading: true,
      /**
       * @type {SideMenuItem[]}
       */
      items: [],
      /**
       * TODO Use OrganizationProps instead
       * @type {OrganizationSideMenuItem[]}
       */
      organizations: [],
      /**
       * @type {OrganizationSideMenuItem | null}
       */
      // @ts-ignore BUG Use null instead of {}
      selectedOrganization: {}
    };
  }

  async componentDidMount() {
    if (!this.props.store) return;
    const { user } = this.props.store;

    await this.setupMenu();
    if (user && user.accessToken && user.organizations) {
      const organizations = user.organizations.map((organization) => ({
        id: organization.id,
        key: organization.id,
        value: organization.fullname,
        text: organization.fullname,
        image: this.renderOrganizationImage(organization, true),
        organization
      }));
      const { selectedOrganizationId } = user;
      const selectedOrganization = organizations.find((organization) => organization.id === selectedOrganizationId);
      this.setState({ organizations, selectedOrganization, loading: false });
    } else {
      this.setState({ loading: false });
    }
  }

  setupMenu = async () => {
    if (!this.props.store) return;
    const { store: { user } } = this.props;
    const selectedOrganization = user && user.accessToken && user.organizations
      && user.organizations.find((organization) => organization.id === user.selectedOrganizationId);
    const isEnrollmentActive = selectedOrganization && (selectedOrganization.enrollmentSetup?.status === 'PAID'
      || selectedOrganization.enrollmentSetup?.status === 'TRIAL');

    const menus = [{
      name: 'classapp',
      routes: [''],
      hover: null,
      key: 1,
      icon: null,
      image: asset('classapp-compact-logo.svg')
    },
    isEnrollmentActive && {
      name: 'matrícula',
      routes: ['periods', 'periodDetail', 'step1', 'step2', 'proposals', 'students', 'message', 'status', 'payment', 'addStudents', 'editMessage'],
      hover: 'Matrícula',
      key: 2,
      icon: 'book user duotone'
    },
    isEnrollmentActive && {
      name: 'transações',
      routes: ['transactions'],
      hover: 'Transações',
      key: 3,
      icon: 'piggy bank duotone'
    }, isEnrollmentActive && {
      name: 'configurações',
      routes: ['settings', 'signatures'],
      hover: 'Configurações',
      key: 4,
      icon: 'cog'
    }];

    this.setState({ items: menus });
  }

  /**
   * @param {OrganizationSideMenuItem} selectedOrganization
   */
  handleChange = (selectedOrganization) => {
    const { router, store } = this.props;

    this.setState({ selectedOrganization });
    User.setSelectedOrganization(selectedOrganization.id);
    // @ts-ignore TODO Check if store.user exists
    store.setUser({ ...store.user, selectedOrganizationId: selectedOrganization.id });

    try {
      if (router.route === '/') window.location.reload();
      else router.push('/');
    } catch (e) {
      router.push('/');
      window.location.reload();
    }
  };

  /**
   * @param {SideMenuItem} item
   * @returns
   */
  _renderItem = (item) => {
    const { currentHover } = this.state;
    const { router } = this.props;
    const splittedRoute = router.route.split('/');
    const isActive = splittedRoute.length > 1 && item.routes.includes(splittedRoute[1]);

    return (
      <Link href={`/${item.routes[0]}`} key={item.name}>
        <a className="side-menu-item">
          <div
            className="menu-selected"
            style={{ display: isActive ? 'block' : 'none' }}
          />
          <div
            className="side-menu-icon"
            style={{ margin: 0 }}
            onMouseEnter={() => this.setState({ currentHover: item.name })}
            onMouseLeave={() => this.setState({ currentHover: null })}
          >
            <div className="side-menu-icon-container">
              {item.image
                ? <Image src={item.image} />
                : (
                  <Icon
                    className={`${item.icon}
                    ${isActive ? 'side-menu-icon-special-color' : ''}`}
                    size="large"
                  />
                )
              }
            </div>
          </div>
          {item.hover
            && (
              <div
                className="side-menu-hover"
                style={{ visibility: item.name === currentHover ? 'visible' : 'hidden' }}
              >
                {item.hover}
              </div>
            )
          }
        </a>
      </Link>
    );
  }

  handleLogout = () => {
    window.location.replace(`${Config.walle.uri[getNodeEnv()]}auth/`);
  }

  /**
   * @param {import('../pages/_app').OrganizationProps} organization
   * @param {boolean} [spaced]
   */
  renderOrganizationImage = (organization, spaced) => (
    <Avatar
      size={28}
      name={organization.fullname}
      src={organization.logoUri}
      className="sb-avatar"
      bg={organization.logoUri && 'fafafa'}
    />
  );

  renderDropdown = () => {
    if (!this.props.store) return null;
    const { loading, selectedOrganization, organizations } = this.state;
    const { user } = this.props.store;

    if (!user || (organizations && organizations.length === 0)) return null;

    if (!selectedOrganization && user.isMaster) return null;

    if (loading || !selectedOrganization) return <Loading width={25} height={25} />;

    return (
      <Dropdown
        className="side-menu-dropdown"
        placeholder="Ordenar por"
        trigger={trigger(selectedOrganization.text, this.renderOrganizationImage(selectedOrganization.organization))}
        pointing="left"
        icon={null}
      >
        <Dropdown.Menu className="side-menu-dropdown-menu" style={{ backgroundColor: '#FFF', maxHeight: '400px', overflowY: 'scroll' }}>
          <Dropdown.Header content="Suas Instituições" />
          {organizations.map((option) => (
            // @ts-ignore TODO options already has the attribute key
            <Dropdown.Item onClick={() => this.handleChange(option)} key={option.value} {...option} style={{ display: 'flex', alignContent: 'center', alignItems: 'center' }} />
          ))}
          <Dropdown.Divider />
          <Dropdown.Item onClick={() => this.handleLogout()} key="logout" icon="sign out" text="Sair" />
        </Dropdown.Menu>
      </Dropdown>
    );
  }

  render() {
    const { items } = this.state;

    return (
      <div
        className="side-menu"
        style={{
          width: 60, flexDirection: 'column', zIndex: 3
        }}
        aria-label="side-menu"
      >
        {items.filter((item) => !!item).map((item) => this._renderItem(item))}

        <div className="side-menu-bottom">
          {this.renderDropdown()}
        </div>
      </div>

    );
  }
}

export default inject('store')(observer(withRouter(SideMenu)));
